/**
 * @module useCloudRoutingSettingsPageQuery
 *
 * This module handles the GraphQL queries related to settings for routing
 * within the cloud. It defines one hook, `useCloudRoutingSettingsPageQuery`,
 * to interact with cloud routing settings for a particular service and graph variant.
 */
import { gql, useQuery } from '@apollo/client';

import {
  CloudRoutingSettingsPageQuery,
  CloudRoutingSettingsPageQueryVariables,
} from 'src/lib/graphqlTypes/types';

/**
 * GraphQL query to fetch routing settings for a specific service ID and graph variant.
 * @type {DocumentNode}
 */
const CLOUD_SETTINGS_PAGE_QUERY = gql`
  query CloudRoutingSettingsPageQuery($serviceId: ID!, $graphVariant: String!) {
    service(id: $serviceId) {
      id
      graphType
      variant(name: $graphVariant) {
        id
        allowedTracks {
          compositionVersion
          minimumRouterVersion
        }
        url
        name
        compositionVersion
        router {
          constants {
            durationBeforeSleepSecs
            durationBeforeDeleteSecs
          }
          customDomains
          endpoints {
            custom
            default
            defaultEnabled
            primary
          }
          id
          gcus
          routerUrl
          shard {
            id
            gcuCapacity
            tier
            region {
              provider
              name
              code
            }
          }
          status
          nextStatus
          routerVersion {
            core
            status
          }
        }
        latestLaunch {
          id
          createdAt
          status
        }
        permissions {
          canManageBuildConfig
          canQueryBuildConfig
        }
      }
    }
  }
`;

/**
 * Hook to execute a query for cloud routing settings.
 *
 * @param {string} serviceId - The ID of the service for which to fetch routing settings.
 * @param {string} graphVariant - The graph variant for which to fetch routing settings.
 * @param {boolean} skip - A flag indicating whether to skip executing the query.
 * @returns {Object} An object containing the following keys:
 *                    - data: Data returned from the query, including settings, shards, etc.
 *                    - loading: Loading state of the query.
 *                    - error: Errors if the query fails.
 */
export const useCloudRoutingSettingsPageQuery = (
  serviceId: string,
  graphVariant: string,
  skip: boolean,
) => {
  return useQuery<
    CloudRoutingSettingsPageQuery,
    CloudRoutingSettingsPageQueryVariables
  >(CLOUD_SETTINGS_PAGE_QUERY, {
    variables: {
      serviceId,
      graphVariant,
    },
    skip,
  });
};
